import { Notify } from "@/components";
import { FormAction, PayXpressState } from "@/models";
import { APIResponseCode } from "@/models";
import { ApiResponse } from "@/models";
import { useGetDataOnClickMutation, usePostDataMutation } from "@/services";
import { useApiStore, useAppStore } from "@/store";
import { camelCaseToTitle } from "@/lib";
import { useCallback, useEffect, useState } from "react";

export const useAPI = <T>() => {
  const [postData, postDataResult] = usePostDataMutation();
  const [getData, getDataResult] = useGetDataOnClickMutation();
  const [callBack, setCallBack] = useState<
    ((apiResponse?: any) => void) | undefined
  >();
  const api = useApiStore((state) => state);
  const { setState, posting } = useAppStore<PayXpressState.BillPaymentPage>(
    (state) => state
  );
  const [data, setData] = useState<T | undefined>();

  const handleGetDataResponse = useCallback(() => {
    try {
      if (getDataResult.data?.totalCount !== undefined) {
        setState("totalCount", getDataResult.data?.totalCount);
      }
      if (getDataResult.data) {
        const response = getDataResult.data;
        if (response.responseCode === APIResponseCode.Success) {
        }
        if (api.callBackFunction) {
          api.callBackFunction(response.data);
        }
        setData(response.data);
        setCallBack(undefined);
        api.setIsProcessing(false);
      } else if (getDataResult.error) {
        setData([] as any);
        api.setIsProcessing(false);
        Notify(
          "Sorry, an error occurred from the server while fetching details",
          false
        );
      } else {
        api.setState("callBackFunction", undefined);
      }
    } catch (error) {
      console.error(`Get Data Response Error ${error}`);
    }
  }, [
    getDataResult.data,
    getDataResult.data?.totalCount,
    getDataResult.error,
    setCallBack,
    callBack,
    api.setState,
    setState,
  ]);

  const callGetData = useCallback(
    (
      endpoint: string,
      callBack?: ((apiResponse?: any) => void) | undefined,
      headers?: any | undefined,
      runCallBackSeparately?: boolean
    ) => {
      try {
        getData({ url: endpoint, headers });
        api.setIsProcessing(true);
        api.setState("callBackFunction", callBack);
        if (runCallBackSeparately) {
          callBack?.(getDataResult.data?.data);
        } else {
          api.setState("callBackFunction", callBack);
        }
      } catch (error) {
        console.error(`Get Call Data Error ${error}`);
      }
    },
    [getData]
  );

  const callPostData = useCallback(
    async (action: FormAction) => {
      try {
        const {
          request,
          url,
          customRequest,
          callBackApiResponse,
          onApiError,
          showToastAfterApiResponse,
          clearPayloadAfterApiSuccessResponse,
        } = action;
        api.setState("isProcessing", true);
        const response = await postData({
          url: url ?? "",
          request: customRequest ?? request,
        });
        const clear =
          clearPayloadAfterApiSuccessResponse ??
          api.clearPayloadAfterApiSuccessResponse;
        api.setAllState({
          ...api,
          isProcessing: false,
          payload: clear ? {} : api.payload,
          customPayload: undefined,
          clearPayloadAfterApiSuccessResponse: false,
        });
        if (
          response.data &&
          response.data?.responseCode === APIResponseCode.Success
        ) {
          if (showToastAfterApiResponse)
            Notify(response.data?.responseMessage, true);
          if (callBackApiResponse) callBackApiResponse(response.data?.data);
        } else if (response.error) {
          const error: any = response?.error;
          if (onApiError) onApiError(error?.data);
          else if (error?.status && error.status === 400) {
            generateBadRequestErrorMessage(error);
          } else {
            Notify(
              "Sorry, Error occurred from the server while posting details",
              false
            );
          }
        } else if (
          onApiError &&
          response.data &&
          response.data?.responseCode !== APIResponseCode.Success
        ) {
          onApiError(response.data);
        } else if (
          response.data &&
          response.data?.responseCode !== APIResponseCode.Success
        ) {
          Notify(response.data?.responseMessage, false);
        }
      } catch (error) {
        console.error(`Post Data Error ${error}`);
      }
    },
    [setState, posting, api]
  );

  const generateBadRequestErrorMessage = (error: any) => {
    if (error?.data?.responseMessage) {
      Notify(error?.data?.responseMessage, false);
    } else {
      const apiError: ApiResponse.Error = error?.data;
      const errorFields: string[] = Object.keys(apiError.errors);
      let message = `${camelCaseToTitle(errorFields[0])} is required`;
      Notify(message.replace("$.", ""), false);
    }
  };

  useEffect(() => {
    handleGetDataResponse();
  }, [handleGetDataResponse]);

  return {
    postData,
    posting: postDataResult.isLoading,
    postDataResponseData: postDataResult?.data?.data,
    fetching: getDataResult.isLoading,
    callPostData,
    getData,
    callGetData,
    data,
  };
};
